import { Button, Spin } from "antd";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { FiActivity, FiAlertTriangle, FiCheckCircle, FiInfo } from "react-icons/fi";
import { IoMdNotificationsOutline } from "react-icons/io";
import { Link } from "react-router-dom";

import { api, endpoint } from "@/api";
import { socket } from "@/api/socket";
import { useSelector } from "react-redux";

const getNotificationStatus = (status) => {
  const s = status?.toLowerCase();
  if (["critical", "warning", "info", "success"].includes(s)) {
    return s;
  }
  return "info";
};

const getStatusIcon = (status) => {
  switch (status) {
    case "warning":
      return <FiActivity className="alert-card-icon-svg" />;
    case "critical":
      return <FiAlertTriangle className="alert-card-icon-svg" />;
    case "success":
      return <FiCheckCircle className="alert-card-icon-svg" />;
    case "info":
    default:
      return <FiInfo className="alert-card-icon-svg" />;
  }
};

const NOTIFICATION_TITLES = {
  offlineDevice: "Device Offline",
  alarm: "Alarm Triggered",
  message: "System Message",
  critical: "Critical Alert",
  warning: "Warning Alert",
  success: "System Update",
  info: "Notification",
};

const getRedirectUrl = (item) => {
  if (item?.section === "offlineDevice") {
    return `/devices/details/${item?.redirectableId}`;
  } else if (item?.section === "alarm") {
    return `/alarm-history`;
  } else if (item?.section === "message") {
    return `/subscription/details/${item?.redirectableId}`;
  }
  return "";
};

const LayoutAlarmNotification = ({ data = {} }) => {
  const {
    chartTitle = "",
    isTitleHidden = false,
    business,
    site,
    locations,
    notificationTypes = ["offlineDevice", "alarm"],
    notificationLimit = 5,
  } = data;

  const targetBusiness = business || locations?.[0]?.business;
  const targetSite = site || locations?.[0]?.site;

  const { userDetails } = useSelector((state) => state.user);
  const [appNotifications, setAppNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  const getNotifications = async () => {
    setIsLoading(true);
    try {
      const typesQuery = Array.isArray(notificationTypes)
        ? notificationTypes.join(",")
        : notificationTypes;

      const params = {
        limit: Number(notificationLimit) || 5,
        page: 1,
      };

      if (targetBusiness) {
        params.business = Array.isArray(targetBusiness) ? targetBusiness.join(",") : targetBusiness;
      }
      if (targetSite && (Array.isArray(targetSite) ? targetSite.length > 0 : true)) {
        params.site = Array.isArray(targetSite) ? targetSite.join(",") : targetSite;
      }
      if (typesQuery) {
        params.types = typesQuery;
      }

      const res = await api.get(endpoint.getAppNotifications, { params });
      if (isMounted.current && res) {
        setAppNotifications(res?.results || []);
        setUnreadCount(res?.unreadCount || 0);
      }
    } catch (error) {
      console.error("Failed to fetch alarm notifications:", error);
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  const onMarkAll = async (id = "all") => {
    try {
      await api.put(endpoint.readAppNotification, { id });
      getNotifications();
      window.dispatchEvent(new CustomEvent("appNotificationRead"));
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    getNotifications();

    const handleMessage = (msgData) => {
      if (msgData?.key === "newAppNotification") {
        getNotifications();
      }
    };

    const handleSync = () => {
      getNotifications();
    };

    socket.on("messageResponse", handleMessage);
    window.addEventListener("appNotificationRead", handleSync);

    return () => {
      isMounted.current = false;
      socket.off("messageResponse", handleMessage);
      window.removeEventListener("appNotificationRead", handleSync);
    };
  }, [
    JSON.stringify(targetBusiness),
    JSON.stringify(targetSite),
    JSON.stringify(notificationTypes),
    notificationLimit,
  ]);

  return (
    <div className="w-100 h-100 d-flex flex-column p-2 overflow-hidden">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0">
        <div className="d-flex align-items-center gap-2">
          {!isTitleHidden && (
            <div className="ql fs-6 fw-bold">
              <div className="ql-editor p-0">
                {chartTitle ? (
                  <div dangerouslySetInnerHTML={{ __html: chartTitle }} />
                ) : (
                  <span>Alarm Notifications</span>
                )}
              </div>
            </div>
          )}
        </div>

        {appNotifications?.length > 0 && unreadCount > 0 && (
          <Button
            type="text"
            danger
            size="small"
            className="alerts-mark-read-btn p-0"
            onClick={() => onMarkAll("all")}
          >
            Mark All As Read
          </Button>
        )}
      </div>

      {/* Content */}
      <div
        className="flex-grow-1 overflow-y-auto alerts-list-container mb-0 nodrag nopan nowheel"
        style={{
          maxHeight: "none",
          height: "100%",
          minHeight: 0,
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
        }}
        onWheel={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {isLoading && !appNotifications.length ? (
          <div className="d-flex align-items-center justify-content-center h-100 p-4">
            <Spin />
          </div>
        ) : !appNotifications?.length ? (
          <div className="d-flex flex-column align-items-center justify-content-center h-100 alerts-empty-state text-muted py-4">
            <IoMdNotificationsOutline className="fs-2 mb-1 opacity-50" />
            <p className="mb-0 small">There are no notifications for you.</p>
          </div>
        ) : (
          appNotifications.slice(0, Number(notificationLimit) || 5).map((item) => {
            const status = getNotificationStatus(item?.status);
            const isUnread = !item?.readers?.some((id) => id === userDetails?._id);
            const title =
              item?.title ||
              NOTIFICATION_TITLES[item?.section] ||
              NOTIFICATION_TITLES[status] ||
              "Notification";
            const subtitle = item?.subtitle || (item?.site?.name ? item.site.name : null);
            const redirectUrl = getRedirectUrl(item);
            const timeText = item?.createdAt ? dayjs(item.createdAt).format("hh:mm A") : "";

            const cardContent = (
              <>
                <div className="alert-card-icon">{getStatusIcon(status)}</div>
                <div className="alert-card-content">
                  <div className="alert-card-header-row">
                    <span className="alert-card-title">{title}</span>
                    <span className="alert-card-time">{timeText}</span>
                  </div>
                  {subtitle && <div className="alert-card-subtitle">{subtitle}</div>}
                  {item?.msg && (
                    <div
                      className="alert-card-message"
                      dangerouslySetInnerHTML={{ __html: item.msg }}
                    />
                  )}
                </div>
              </>
            );

            return redirectUrl ? (
              <Link
                key={item?._id}
                to={redirectUrl}
                className={`alert-card alert-card-${status} ${isUnread ? "alert-unread" : "alert-read"}`}
                onClick={() => onMarkAll(item?._id)}
              >
                {cardContent}
              </Link>
            ) : (
              <div
                key={item?._id}
                className={`alert-card alert-card-${status} ${isUnread ? "alert-unread" : "alert-read"}`}
                onClick={() => onMarkAll(item?._id)}
              >
                {cardContent}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LayoutAlarmNotification;

LayoutAlarmNotification.propTypes = {
  data: PropTypes.object,
};
